import http from "./http-common";
import JWT from "./Jwt";
import Crypto from "./Crypto"
import { getServidor, getServidorPorAmbiente } from "../utils/Utils";

/**
 * @class UsersDataService
 * @brief Servicio para consumir los endpoints de usuarios y sus configuraciones.
 */

class UsersDataService {

    /**
     * Endpoints de Usuario
     */

    /**
     * Método que obtiene todos los usuarios registrados en el sistema
     * @param idAdmin Identificador del administrador que realiza la consulta
     * @param key Clave para encriptación AES
     * @returns JSON con el listado de usuarios
     */
    async getAll(idAdmin, key) {
        const datosEncriptados = await Crypto.encriptar(idAdmin, key);
        return http.get(`${getServidor()}/usuarios/`, {
            params: datosEncriptados
        });
    }
    /**
     * Método que obtiene los datos de un usuario
     * @param key clave para encriptacion AES 
     * @returns JSON de datos y configuraciones de usuarios
     */
    async getUsuario(key) {
        const datosEncriptados = await Crypto.encriptar(sessionStorage.username, key);
        return http.get(`${getServidor()}/usuarios/byMatricula`, {
            params: datosEncriptados
        });
    }
    /**
     * Método que obtiene el listado de administradores del sistema
     * @returns JSON con el listado de administradores
     */
    getAdministradores() {
        return http.get(`${getServidor()}/usuarios/administradores`);
    }
    /**
     * Método que obtiene los datos del usuario autenticado actualmente
     * @returns JSON con los datos del usuario en sesión
     */
    getCurrentUser() {
        return http.get(`${getServidor()}/usuarios/getCurrentUser`);
    }
    /**
     * Método que obtiene los usuarios filtrados por rol y área
     * @param idArea Identificador del área
     * @param key Clave para encriptación AES
     * @returns JSON con el listado de usuarios que coinciden con el rol y el área
     */
    async getUsuariosByRolAndArea(idArea, key) {
        const datosEncriptados = await Crypto.encriptar(idArea, key);
        return http.get(`${getServidor()}/usuarios/usuariosByRolAndArea`, {
            params: datosEncriptados
        });
    }
    /**
     * Método que obtiene los usuarios por rol y área asociados al administrador en sesión
     * @returns JSON con el listado de usuarios del administrador
     */
    getUsuariosByAdminByRolAndArea() {
        return http.get(`${getServidor()}/usuarios/usuariosAdmin`);
    }

    /**
     * Método que crea un usuario
     * @param data Datos del usuario nuevo
     * @param key Clave para encriptación AES
     * @returns true-false
     */
    async create(data, key) {
        const datosEncriptados = await Crypto.encriptar(data, key);
        return http.post(`${getServidor()}/usuarios/`, datosEncriptados);
    }

    /**
    * Método que obtiene los datos de un usuario para notificaciones dado un token
    * @returns JSON de datos y configuraciones de usuarios
    */
    async getByToken(data, key) {
        const datosEncriptados = await Crypto.encriptar(data, key);
        return http.get(`${getServidorPorAmbiente()}/usuariosNotificaciones/obtenerPorToken`, {
            params: datosEncriptados
        });
    }
    /**
     * Método que verifica si ya existe un token de notificación para un usuario 
     * @param sistema - Sistema operativo del dispositivo
     * @returns 
     */
    existeTokenNotificacion(sistema) {
        return http.get(`${getServidor()}/usuariosNotificaciones/obtenerPorMatriculaAndSO/${sistema}`);
    }

    /**
     * Sends a POST request to create a push notification token.
     * @param data - The data to be encrypted and sent in the request.
     * @param key - The encryption key used to encrypt the data.
     * @returns {Promise}
     */
    async createTokenPush(data, key) {
        const datosEncriptados = await Crypto.encriptar(data, key);
        return http.put(`${getServidorPorAmbiente()}/usuariosNotificaciones/`, datosEncriptados);
    }
    /**
     * Método que elimina al usuario en sesión (petición asíncrona)
     * @returns true-false
     */
    delete() {
        return http.delete(localStorage.getItem('servidor') + "/asincrona/usuarios/" + JWT.jwtEncode(sessionStorage.id));
    }
    /**
     * Método que actualiza los datos de un usuario
     * @param data Datos del usuario que se actualizarán
     * @param key Clave para encriptación AES
     * @returns true-false
     */
    async update(data, key) {
        const datosEncriptados = await Crypto.encriptar(data, key);
        return http.put(`${getServidor()}/usuarios/`, datosEncriptados)
    }

    /**
     * Método que permite habilitar un usuario en el sistema
     * @param key Clave para encriptación AES
     * @returns true-false
     */
    async habilitar(key) {
        const datosEncriptados = await Crypto.encriptar(sessionStorage.matricula, key);
        return http.put(`${getServidor()}/usuarios/habilitar/`, datosEncriptados)
    }
    /**
     * Método que retorna los roles de usuarios
     * @returns JSON con los roles de usuarios
     */
    getRoles() {
        return http.get(`${getServidor()}/usuarios/roles`); //listado de roles
    }
    /**
     * Método que permite generar una contraseña temporal
     * @param id Identificador único del usuario
     * @param key Clave para encriptación AES
     * @returns la nueva contraseña
     */
    async resetearContrasena(id, key) {
        const datosEncriptados = await Crypto.encriptar(id, key);
        return http.get(`${getServidor()}/usuarios/temporal`, {
            params: datosEncriptados
        });
    }

    /**
    * Método que permite cambiar la contraseña 
    * @param data Nueva contraseña
    * @param key Clave para encriptación AES
    * @returns true-false
    */
    async cambiarContrasena(data, key) {
        const datosEncriptados = await Crypto.encriptar(data, key);
        return http.put(`${getServidor()}/usuarios/resetear`, datosEncriptados);
    }

    /**
    * Método que obtiene el valor de configuración de tiempo real
    * @returns true-false
    */
    getUbicacionTiempoReal() {
        return http.get(`${getServidor()}/usuarios/tiempoReal`);
    }
    /**
      * Método que cambia el valor de configuración de tiempo real
      * @returns true-false
      */
    habilitarUbicacionTiempoReal() {
        return http.get(`${getServidor()}/usuarios/habilitarTiempoReal`)
    }

    /**
     * Método que obtiene el valor de configuración del tema oscuro
     * @returns true-false
     */
    getTemaOscuro() {
        return http.get(`${getServidor()}/usuarios/temaOscuro`)
    }

    /**
     * Método que cambia el valor de configuración del tema actual del sistema.
     * @returns true-false
     */
    habilitarTemaOscuro() {
        return http.get(`${getServidor()}/usuarios/habilitarTemaOscuro`)
    }

    /**
     * Método que obtiene el valor de configuración del idioma actual
     * @returns Código o valor del idioma seleccionado
     */
    getIdiomaActual() {
        return http.get(`${getServidor()}/usuarios/idiomaActual`)
    }

    /**
     * Método que cambia el valor de configuración del idioma actual del sistema.
     * @returns true-false
     */
    habilitarCambioIdioma() {
        return http.get(`${getServidor()}/usuarios/habilitarCambioIdioma`)
    }

    /**
     * Metodo que retorna el valor actual de la configuracion de alarma audible.
     * @returns alarmaAudible
     */
    getAlarmaAudible() {
        return http.get(`${getServidor()}/usuarios/alarmaAudible`)
    }
    /**
    * Metodo que actualiza el valor de configuracion de alarma audible
    * @returns true o false
    */
    habilitarAlarmaAudible() {
        return http.get(`${getServidor()}/usuarios/habilitaAlarmaAudible`)
    }

    /**
     * Metodo que permite visualizar el perfil de otro usuario
     * @returns JSON con los datos del usuario
     */
    async filtroUsuario(id, key) {
        const datosEncriptados = await Crypto.encriptar(id, key);
        return http.get(`${getServidor()}/usuarios/master`, {
            params: datosEncriptados
        });
    }

    /**
     * Método que envía una notificación al usuario en sesión
     * @param key Clave para encriptación AES
     * @returns true-false
     */
    async notificar(key) {
        const datosEncriptados = await Crypto.encriptar(sessionStorage.username, key);
        return http.get(`${getServidor()}/usuarios/notificar/`, {
            params: datosEncriptados
        })
    }

    /**
     * Método que elimina el token de un usuario para cerrar sus sesiones
     * @param usuario Datos del usuario (se utiliza su matrícula)
     * @param key Clave para encriptación AES
     * @returns true-false
     */
    async cerrarSesionUsuarios(usuario, key) {
        const data = {
            matricula: usuario.matricula
        }
        // Reemplaza 'TU_LLAVE_AQUI' por el string que tienes en sessionStorage
        console.log("Cerrar sesion:");
        
        console.log(atob(key).length);
        const datosEncriptados = await Crypto.encriptar(data, key);
        return http.post(`${getServidor()}/usuarios/cerrarSesionUsuario`, datosEncriptados);
    }

}

export default new UsersDataService();